import { useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquarePlus, X, Send, Loader2, Star, Check } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "general", label: "Allgemeines Feedback" },
  { value: "bug", label: "Fehler melden" },
  { value: "feature", label: "Feature-Wunsch" },
  { value: "praise", label: "Lob" },
];

export function BetaFeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const location = useLocation();

  const reset = () => {
    setCategory("");
    setRating(0);
    setHoverRating(0);
    setMessage("");
    setSent(false);
  };

  const submit = async () => {
    if (!category) { toast.error("Bitte waehlen Sie eine Kategorie"); return; }
    setSending(true);
    try {
      await api.post("/beta-feedback", {
        category,
        rating: rating || null,
        message: message.trim() || null,
        page: location.pathname,
      });
      setSent(true);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("Bitte warten Sie etwas, bevor Sie weiteres Feedback senden.");
      } else {
        toast.error(err.response?.data?.detail || "Feedback konnte nicht gesendet werden");
      }
    }
    setSending(false);
  };

  const close = () => { setOpen(false); setTimeout(reset, 300); };

  if (sent && open) {
    return (
      <div className="fixed bottom-5 right-5 z-[90] w-80 bg-card border border-border rounded-2xl shadow-xl overflow-hidden" data-testid="feedback-success">
        <div className="p-5 text-center">
          <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-3">
            <Check className="w-6 h-6 text-success" />
          </div>
          <p className="font-heading text-base font-bold mb-1">Vielen Dank!</p>
          <p className="font-body text-sm text-muted-foreground">Wir lesen jedes Feedback persoenlich und arbeiten staendig daran, Spurwechsel zu verbessern.</p>
          <Button variant="ghost" size="sm" className="font-body text-sm mt-3" onClick={close} data-testid="beta-feedback-close-btn">
            Schliessen
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[90] bg-primary text-primary-foreground rounded-full shadow-lg w-12 h-12 flex items-center justify-center hover:bg-primary/90 transition-all hover:scale-105 active:scale-95"
          data-testid="beta-feedback-trigger-btn"
          title="Feedback geben"
        >
          <MessageSquarePlus className="w-5 h-5" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-[90] w-80 bg-card border border-border rounded-2xl shadow-xl overflow-hidden" data-testid="beta-feedback-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-muted/30">
            <p className="font-heading text-sm font-bold">Beta-Feedback</p>
            <button onClick={close} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="beta-feedback-close-x">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-3">
            <p className="font-body text-xs text-muted-foreground">
              Ihr Feedback wird vertraulich behandelt und hilft uns, das Portal zu verbessern.
            </p>

            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 font-body text-sm" data-testid="beta-feedback-category">
                <SelectValue placeholder="Kategorie waehlen *" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value} className="font-body text-sm">{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div>
              <p className="font-body text-xs text-muted-foreground mb-1.5">Gesamteindruck (optional)</p>
              <div className="flex gap-1" data-testid="beta-feedback-rating">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    onMouseEnter={() => setHoverRating(n)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(n === rating ? 0 : n)}
                    className="p-0.5 transition-transform hover:scale-110"
                    data-testid={`beta-feedback-star-${n}`}
                  >
                    <Star
                      className={`w-5 h-5 transition-colors ${
                        n <= (hoverRating || rating) ? "text-warning fill-warning" : "text-muted-foreground/30"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Textarea
                value={message}
                onChange={e => setMessage(e.target.value.slice(0, 1000))}
                placeholder="Ihre Nachricht (optional, max. 1000 Zeichen)"
                className="min-h-[80px] font-body text-sm resize-none"
                data-testid="beta-feedback-message"
              />
              {message.length > 0 && (
                <p className="font-body text-[10px] text-muted-foreground text-right mt-0.5">{message.length}/1000</p>
              )}
            </div>

            <Button
              onClick={submit}
              disabled={!category || sending}
              className="w-full h-9 font-body text-sm bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5"
              data-testid="beta-feedback-submit-btn"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Feedback senden
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
